// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

// One consistent pill shape for every status — same size/weight, differing only
// by hue, so the column reads as a calm legend rather than a mix of solid/outline
// chips. whitespace-nowrap keeps a label on a single line (no more wrapped pills).
const badgeVariants = cva(
  'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
  {
    variants: {
      variant: {
        unopened:      'bg-amber-500/12 text-amber-700 dark:text-amber-300',
        completed:     'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
        'in-progress': 'bg-blue-500/12 text-blue-700 dark:text-blue-300',
        reviewed:      'bg-violet-500/12 text-violet-700 dark:text-violet-300',
        default:       'bg-muted text-muted-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  }
)

interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge, badgeVariants }
